import { useField } from 'formik';
import { useMemo, useCallback, useState, useEffect, Fragment } from 'react';
import { Button, Alert } from "react-bootstrap";
import Icon from "../../../controls/Icon";
import { useFormsConfig } from '../Context';
import { useSharedSection } from '../SharedSectionContext';
import GenericDropdown from '../../../controls/GenericDropdown';
import GenericTextBox from '../../../controls/GenericTextBox';
import Tooltip from '../../../controls/ToolTip';
import { useTranslation } from 'react-i18next';
import _ from "lodash";
import DatePicker from 'react-datepicker';
import ListStatus from '../controls/ListStatus';
import { updateConditionIds } from '../utils';

const operationsList = [
    {
        _id: 'is-set',
        name: "is set"
    },
    {
        _id: 'is-unset',
        name: "is not set"
    },
    {
        _id: 'greater-than',
        name: 'greater than',
    },
    {
        _id: 'greater-than-equals',
        name: 'greater than or equal',
    },
    {
        _id: 'less-than',
        name: 'less than',
    },
    {
        _id: 'less-than-equals',
        name: 'less than or equal',
    },
    {
        _id: 'equals',
        name: 'equal',
    },
    {
        _id: 'not-equals',
        name: 'not equal',
    },
    {
        _id: 'includes',
        name: 'includes'
    }
];

const timeFrameOptions = (dataType) => [
    {
        _id: 'now',
        name: dataType === 'date' ? 'today' : 'datetime now',
    },
    {
        _id: 'specific-date',
        name: `the specific ${dataType.replace('-', '')}`,
    },
    {
        _id: 'relative-date',
        name: `a relative ${dataType.replace('-', '')} of`,
    }
];

const relativeTimePeriodOptions = [
    {
        _id: 'past',
        name: 'ago',
    },
    {
        _id: 'future',
        name: 'from now',
    },
]

const relativeValueOptions = [
    {
        _id: "false",
        name: "against the value"
    },
    {
        _id: "true",
        name: "relative to"
    },
];

const CustomConditionControl = ({
    name,
    conditionKeys = [],
    onChange,
    disabled,
    item = {},
    isForItem = true,
    sectionId = '',
    addNewItem,
    itemReadOnly = false,
    includeCurrentSection = true,
    label,
    labelNoStatements
}) => {
    const [translate] = useTranslation();
    const [field, meta, { setValue, setTouched, }] = useField(name);
    const { formTemplate, dataLists, dataTypesWithOperationsList } = useFormsConfig();

    const { sharedSection } = useSharedSection();

    const [orConditions, setOrConditions] = useState(_.cloneDeep(field.value));
    const [canCopy, setCanCopy] = useState(false);
    const [copyOptionId, setCopyOptionId] = useState();

    const filterSections = useCallback(() => includeCurrentSection ? () => true : (section) => section._id !== sectionId, [includeCurrentSection, sectionId]);
    const copyOptions = useMemo(() => {
        const allOptions = (isForItem ? formTemplate?.sections.find((section) => section._id === sectionId)?.items : formTemplate?.sections) || [];

        return allOptions.filter((option) => option._id !== sectionId && option._id !== item._id && option[name] && option[name].length > 0 && option[name].at(0).length > 0);
    }, [isForItem, formTemplate?.sections, sectionId, item._id, name]);

    useEffect(() => {
        if (name) {
            setOrConditions(_.cloneDeep(field.value))
        }
    }, [name, field.value]);

    useEffect(() => {
        setCanCopy(copyOptions.length > 0 && !(orConditions && orConditions.length > 0 && orConditions.at(0).length > 0));
    }, [copyOptions, orConditions]);

    const fieldTouched = useCallback((orIndex, andIndex, fieldName) => {
        return orConditions?.[orIndex]?.[andIndex]?.[`_touched_${fieldName}`] ? true : false;
    }, [orConditions]);

    const error = useCallback((orIndex, andIndex) => {
        if (meta.touched && meta.error && meta.value && meta.value.length > 0) {
            let messages = {}

            if (meta.error[orIndex]) {
                const andConditionErrors = meta.error[orIndex][andIndex];
                if (andConditionErrors && Object.keys(andConditionErrors).length > 0) {
                    Object.keys(andConditionErrors).forEach(fieldName => {
                        if (fieldTouched(orIndex, andIndex, fieldName)) {
                            messages[fieldName] = andConditionErrors[fieldName];
                        }
                    });
                }
            }

            return <div className="w-100">
                <div className='text-danger small'>{Object.values(messages).join(", ")}</div>
            </div>;
        }

        return null;
    }, [meta, fieldTouched]);

    const getErrorBorder = useCallback((fieldName, orIndex, andIndex) => {
        if (!meta.error) {
            return '';
        }

        if (meta?.error.length <= 0 || !meta?.error[orIndex] || meta?.error[orIndex].length <= 0) {
            return '';
        }

        if (!meta?.error[orIndex][andIndex] || !meta?.error[orIndex][andIndex][fieldName]) {
            return '';
        }

        return 'border-danger';
    }, [meta]);

    const blurConditionField = useCallback((orIndex, andIndex, fieldName) => {
        const updatingAndCondition = {
            ...orConditions[orIndex][andIndex],
            [`_touched_${fieldName}`]: true
        };

        const updatingOrCondition = [...orConditions[orIndex].slice(0, andIndex), updatingAndCondition, ...orConditions[orIndex].slice(andIndex + 1)];
        const result = [...orConditions.slice(0, orIndex), updatingOrCondition, ...orConditions.slice(orIndex + 1)];

        setOrConditions(result);
        setTouched(true);
    }, [orConditions, setOrConditions, setTouched]);

    const handleFieldChange = useCallback((newValue) => {
        setTouched(true);
        setValue(newValue);
        if (onChange) {
            onChange(newValue);
        }
    }, [onChange, setValue, setTouched]);

    const updateConditionField = useCallback((orIndex, andIndex, objToUpdate) => {
        const updatingAndCondition = {
            ...orConditions[orIndex][andIndex]
        };

        Object.keys(objToUpdate).forEach(key => {
            updatingAndCondition[key] = objToUpdate[key];
            updatingAndCondition[`_touched_${key}`] = true;
        });

        const updatingOrCondition = [...orConditions[orIndex].slice(0, andIndex), updatingAndCondition, ...orConditions[orIndex].slice(andIndex + 1)];
        const result = [...orConditions.slice(0, orIndex), updatingOrCondition, ...orConditions.slice(orIndex + 1)];

        setOrConditions(result);
        handleFieldChange(result);
    }, [orConditions, handleFieldChange]);

    const addCondition = useCallback((orIndex) => {
        const emptyCondition = {};
        conditionKeys.forEach(key => {
            emptyCondition[key] = '';
            if (key === 'section') {
                emptyCondition[key] = sectionId;
            }
            if (key === 'item') {
                emptyCondition[key] = name === 'validation' && dataTypesWithOperationsList[item.dataType].length !== 0 ? item._id : null;
            }
            if (key === 'relativeTimePeriod') {
                emptyCondition[key] = "future";
            }
            if (key === 'relativeValue') {
                emptyCondition[key] = "false";
            }
            if (key === 'relativeSection') {
                emptyCondition[key] = null;
            }
            if (key === 'relativeItem') {
                emptyCondition[key] = null;
            }
        });
        const updatingOrCondition = [...orConditions[orIndex], { ...emptyCondition }];
        const result = [...orConditions.slice(0, orIndex), updatingOrCondition, ...orConditions.slice(orIndex + 1)];

        setOrConditions(result);
        handleFieldChange(result);
    }, [name, orConditions, item, dataTypesWithOperationsList, sectionId, conditionKeys, handleFieldChange]);

    const removeCondition = useCallback((orIndex, andIndex) => {
        const updatingOrCondition = orConditions[orIndex].filter((_andStatement, i) => i !== andIndex);
        const result = [...orConditions.slice(0, orIndex), updatingOrCondition, ...orConditions.slice(orIndex + 1)];

        setOrConditions(result);
        handleFieldChange(result);
    }, [orConditions, handleFieldChange]);

    const addGrouping = useCallback(() => {
        const emptyCondition = {};
        conditionKeys.forEach(key => {
            emptyCondition[key] = '';
            if (key === 'section' && includeCurrentSection) {
                emptyCondition[key] = sharedSection ? sharedSection._id : sectionId;
            }
            if (key === 'item') {
                emptyCondition[key] = name === 'validation' && dataTypesWithOperationsList[item.dataType].length !== 0 ? item._id : null;
            }
            if (key === 'relativeTimePeriod') {
                emptyCondition[key] = "future";
            }
            if (key === 'relativeValue') {
                emptyCondition[key] = "false";
            }
            if (key === 'relativeSection') {
                emptyCondition[key] = null;
            }
            if (key === 'relativeItem') {
                emptyCondition[key] = null;
            }
        });
        const result = [...orConditions, [{ ...emptyCondition }]];

        setOrConditions(result);
        handleFieldChange(result);
    }, [name, orConditions, item, sectionId, includeCurrentSection, sharedSection, dataTypesWithOperationsList, conditionKeys, handleFieldChange]);

    const removeGrouping = useCallback((orIndex) => {
        const result = orConditions.filter((_orStatement, j) => j !== orIndex);

        setOrConditions(result);
        handleFieldChange(result);
    }, [orConditions, handleFieldChange]);

    const _handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
        }
    }

    const getItemsWithCurrentUpdates = useCallback((forSectionId) => {
        let items = sharedSection ? sharedSection.items : [...(formTemplate.sections.find(section => section._id === forSectionId)?.items || [])];

        items = items.filter(item => {
            // Not defined so we show it
            if (!dataTypesWithOperationsList[item.dataType]) {
                return true
            }
            // Defined but set to empty so no show
            if (dataTypesWithOperationsList[item.dataType].length === 0) {
                return false;
            }
            return true
        });

        if (addNewItem && forSectionId === sectionId) {
            items.push({
                ...item,
                name: item.name || translate("OTM_THIS_ITEM")
            });
        }

        return items;
    }, [formTemplate?.sections, sharedSection, item, sectionId, addNewItem, dataTypesWithOperationsList, translate]);

    const getSelectedItem = useCallback((condition) => {
        const items = getItemsWithCurrentUpdates(condition.section);
        return items.find((sectionItem) => sectionItem._id === condition.item);
    }, [getItemsWithCurrentUpdates]);

    const renderDatePhrase = useCallback((dataType, condition, i, j) => {
        switch (condition.timeFrame) {
            case 'relative-date': {
                return <>
                    <RequiredIndicator>
                        <GenericTextBox
                            defaultValue={condition.value}
                            disabled={disabled}
                            onKeyDown={_handleKeyDown}
                            onBlur={() => { blurConditionField(i, j, 'value') }}
                            onChange={(event) => { updateConditionField(i, j, { 'value': event.target.value }) }}
                            placeholder={translate(dataType === 'date' ? 'OTM_CONDITION_RELATIVE_DATE_PLACEHOLDER' : (dataType === 'time' ? 'OTM_CONDITION_RELATIVE_TIME_PLACEHOLDER' : 'OTM_CONDITION_RELATIVE_DATETIME_PLACEHOLDER'))}
                            css={{ input: `form-control-sm ${getErrorBorder('value', i, j)}` }}
                        />
                    </RequiredIndicator>
                    <Tooltip text={translate(dataType === 'date' ? 'OTM_RELATIVE_DATE_TOOLTIP' : (dataType === 'time' ? 'OTM_RELATIVE_TIME_TOOLTIP' : 'OTM_RELATIVE_DATETIME_TOOLTIP'))} />

                    <RequiredIndicator>
                        <GenericDropdown
                            size="sm"
                            defaultValue={condition.relativeTimePeriod}
                            options={relativeTimePeriodOptions}
                            readOnly={disabled}
                            onSelect={(value) => { updateConditionField(i, j, { 'relativeTimePeriod': value }); }}
                            valueKey="_id"
                            displayValueKey="name"
                            css={{ toggle: `${getErrorBorder('relativeTimePeriod', i, j)}` }}
                        />
                    </RequiredIndicator>
                </>
            }
            case 'specific-date': {
                return <RequiredIndicator>
                    <DatePicker
                        selected={condition.value && new Date(condition.value)}
                        disabled={disabled}
                        onChange={(newDate) => { updateConditionField(i, j, { 'value': newDate.toString() }) }}
                        showDatePicker={dataType === 'date' || dataType === 'date-time'}
                        showTimeSelect={dataType === 'time' || dataType === 'date-time'}
                        showTimeSelectOnly={dataType === 'time'}
                        dateFormat={dataType === 'date-time' ? "yyyy-MMM-dd HH:mm" : dataType === 'date' ? "yyyy-MMM-dd" : "HH:mm:ss"}
                        className={`form-control form-control-sm ${getErrorBorder('value', i, j)}`}
                    />
                </RequiredIndicator>
            }
            case 'now':
            default: {
                return null;
            }
        }
    }, [disabled, getErrorBorder, updateConditionField, translate, blurConditionField]);

    const getOperationOptions = useCallback((condition) => {
        const item = getSelectedItem(condition)
        let options = !dataTypesWithOperationsList[item?.dataType] ?
            operationsList : dataTypesWithOperationsList[item?.dataType];

        if (item?.dataType === 'select-option' && item?.multipleOptions === true) {
            options = options.filter(opt => !['equals', 'not-equals'].includes(opt._id));
        }
        else if (item?.dataType === 'select-option' && item?.multipleOptions === false) {
            options = options.filter(opt => !['includes'].includes(opt._id));
        }
        return options;
    }, [dataTypesWithOperationsList, getSelectedItem]);

    const showConditionValue = useCallback((condition) => {
        if (['is-set', 'is-unset'].includes(condition.operation)) {
            return false;
        }
        const operations = getOperationOptions(condition);
        var allowsValue = false;
        for (var i = 0; i < operations.length; i++) {
            if (!['is-set', 'is-unset'].includes(operations[i]._id)) {
                allowsValue = true;
            }
        }
        if (!allowsValue) {
            return false;
        }

        return true;
    }, [getOperationOptions]);

    const renderRelativeValuePhrase = useCallback((condition, i, j) => {
        if (!showConditionValue(condition)) {
            return null;
        }

        const conditionItem = getSelectedItem(condition);
        const relativeSection = formTemplate.sections.find(section => section._id === condition.relativeSection);
        const relativeItems = relativeSection?.items?.filter((item) => item.dataType === conditionItem.dataType);
        if (relativeItems) {
            let currentItemIndex = relativeItems.findIndex(o => o._id === conditionItem._id);
            if (currentItemIndex !== -1) {
                relativeItems[currentItemIndex] = conditionItem;
            }
        }

        return <>
            <span>{translate("OTM_CUSTOM_CONDITION_CONTROL_PHRASES_4")}</span>
            <RequiredIndicator>
                <GenericDropdown
                    valueKey="_id"
                    displayValueKey="name"
                    defaultValue={condition.relativeSection}
                    options={formTemplate.sections}
                    readOnly={disabled}
                    onSelect={(value) => { updateConditionField(i, j, { 'relativeSection': value, 'relativeItem': null }); }}
                    size="sm"
                    css={{ toggle: `${getErrorBorder('relativeSection', i, j)}` }}
                />
            </RequiredIndicator>
            <span>{translate("OTM_CUSTOM_CONDITION_CONTROL_PHRASES_5")}</span>
            <RequiredIndicator>
                <GenericDropdown
                    valueKey="_id"
                    displayValueKey="name"
                    defaultValue={condition.relativeItem}
                    options={relativeItems}
                    readOnly={disabled}
                    onSelect={(value) => { updateConditionField(i, j, { 'relativeItem': value }) }}
                    size={"sm"}
                    css={{ toggle: `${getErrorBorder('relativeItem', i, j)}` }}
                />
            </RequiredIndicator>
        </>
    }, [formTemplate?.sections, getSelectedItem, disabled, showConditionValue, getErrorBorder, updateConditionField, translate]);

    const renderStrictValuePhrase = useCallback((condition, i, j) => {
        if (!showConditionValue(condition)) {
            return null;
        }
        const selectedItem = getSelectedItem(condition);

        switch (selectedItem?.dataType) {
            case 'date':
            case 'time':
            case 'date-time': {
                return <>
                    <label>{translate("OTM_CUSTOM_CONDITION_CONTROL_PHRASES_6")}</label>
                    <RequiredIndicator>
                        <GenericDropdown
                            size="sm"
                            defaultValue={condition.timeFrame}
                            options={timeFrameOptions(selectedItem.dataType)}
                            readOnly={disabled}
                            onSelect={(value) => {
                                const objToUpdate = {
                                    'timeFrame': value,
                                    'value': ''
                                }
                                if (value === 'relative-date') {
                                    objToUpdate['relativeTimePeriod'] = 'future';
                                }
                                updateConditionField(i, j, objToUpdate);
                            }}
                            valueKey="_id"
                            displayValueKey="name"
                            css={{ toggle: `${getErrorBorder('timeFrame', i, j)} ${!condition.timeFrame ? 'border-danger' : ''}` }}
                        />
                    </RequiredIndicator>
                    {renderDatePhrase(selectedItem.dataType, condition, i, j)}
                </>
            }
            case 'select-option': {
                var selectedDataList = {
                    options: []
                };
                if (selectedItem?.dataList) {
                    selectedDataList = dataLists.find(list => list._id === selectedItem.dataList || list._id === selectedItem.dataList._id);
                }
                return <RequiredIndicator>
                    <GenericDropdown
                        size="sm"
                        defaultValue={condition.value}
                        options={selectedDataList.options}
                        readOnly={disabled}
                        onSelect={(value) => { updateConditionField(i, j, { 'value': value }) }}
                        valueKey="value"
                        displayValueKey="displayValue"
                        css={{ toggle: `${getErrorBorder('value', i, j)}` }}
                    />
                </RequiredIndicator>
            }
            case 'number': {
                return <RequiredIndicator>
                    <GenericTextBox
                        type="number"
                        defaultValue={condition.value}
                        disabled={disabled}
                        onKeyDown={_handleKeyDown}
                        onBlur={() => { blurConditionField(i, j, 'value') }}
                        onChange={(event) => { updateConditionField(i, j, { 'value': event.target.value }) }}
                        css={{ input: `form-control-sm ${getErrorBorder('value', i, j)}` }} />
                </RequiredIndicator>
            }
            case 'single-line-text':
            case 'multi-line-text':
            case 'email':
            case 'phone':
            default: {
                return <RequiredIndicator>
                    <GenericTextBox
                        defaultValue={condition.value}
                        disabled={disabled}
                        onKeyDown={_handleKeyDown}
                        onBlur={() => { blurConditionField(i, j, 'value') }}
                        onChange={(event) => { updateConditionField(i, j, { 'value': event.target.value }) }}
                        css={{ input: `form-control-sm ${getErrorBorder('value', i, j)}` }} />
                </RequiredIndicator>
            }
        }
    }, [getSelectedItem, disabled, dataLists, getErrorBorder, showConditionValue, renderDatePhrase, updateConditionField, translate, blurConditionField]);

    const renderConditions = useCallback((andConditions, i) => {
        return !andConditions || andConditions.length === 0 ?
            <ListStatus
                loading={false}
                error={false}
                empty={true}
                emptyText={translate('OTM_NO_CONDITIONS')}
            ></ListStatus> : <>
                {andConditions.map((condition, j) => {
                    const items = getItemsWithCurrentUpdates(condition.section);

                    return <Fragment key={`${name}_condition_${i}_${j}`}>
                        <div className="d-flex align-items-end">
                            <div className="flex-grow-1 sentence">
                                <label>{translate("OTM_CUSTOM_CONDITION_CONTROL_PHRASES_1")}</label>

                                <RequiredIndicator>
                                    <GenericDropdown
                                        valueKey="_id"
                                        displayValueKey="name"
                                        defaultValue={condition.section}
                                        options={sharedSection ? [sharedSection] : formTemplate.sections.filter(filterSections)}
                                        readOnly={!!sharedSection || disabled}
                                        onSelect={(value) => {
                                            updateConditionField(i, j, { 'section': value, 'item': null, 'timeFrame': null });
                                        }}
                                        size="sm"
                                        css={{ toggle: `${getErrorBorder('section', i, j)}` }}
                                    />
                                </RequiredIndicator>


                                <label>{translate("OTM_CUSTOM_CONDITION_CONTROL_PHRASES_2")}</label>

                                <RequiredIndicator>
                                    <GenericDropdown
                                        valueKey="_id"
                                        displayValueKey="name"
                                        defaultValue={condition.item}
                                        options={items}
                                        readOnly={disabled}
                                        onSelect={(value) => {
                                            const newOperations = getOperationOptions({ ...condition, item: value, timeFrame: null });
                                            if (!newOperations.map(op => op._id).includes(condition.operation)) {
                                                updateConditionField(i, j, { 'item': value, 'timeFrame': null, 'operation': '' });
                                            }
                                            else {
                                                updateConditionField(i, j, { 'item': value, 'timeFrame': null });
                                            }
                                        }}
                                        size={"sm"}
                                        css={{ toggle: `${getErrorBorder('item', i, j)}` }}
                                    />
                                </RequiredIndicator>

                                <label>{translate("OTM_CUSTOM_CONDITION_CONTROL_PHRASES_3")}</label>

                                <RequiredIndicator>
                                    <GenericDropdown
                                        valueKey="_id"
                                        displayValueKey="name"
                                        defaultValue={condition.operation}
                                        options={getOperationOptions(condition)}
                                        readOnly={disabled}
                                        onSelect={(value) => {
                                            if (value === 'is-set' || value === 'is-unset') {
                                                updateConditionField(i, j, { 'relativeValue': 'false', 'operation': value })
                                            }
                                            else {
                                                updateConditionField(i, j, { 'operation': value });
                                            }
                                        }}
                                        size={"sm"}
                                        css={{ toggle: `${getErrorBorder('operation', i, j)}` }}
                                    />
                                </RequiredIndicator>

                                {showConditionValue(condition) && <RequiredIndicator>
                                    <GenericDropdown
                                        valueKey="_id"
                                        displayValueKey="name"
                                        defaultValue={condition.relativeValue.toString()}
                                        options={relativeValueOptions}
                                        readOnly={disabled}
                                        onSelect={(value) => { updateConditionField(i, j, { 'relativeValue': value }); }}
                                        size={"sm"}
                                        css={{ toggle: `${getErrorBorder('relativeValue', i, j)}` }}
                                    />
                                </RequiredIndicator>}

                                {condition.relativeValue.toString() === 'true' ?
                                    renderRelativeValuePhrase(condition, i, j) :
                                    renderStrictValuePhrase(condition, i, j)}

                                {conditionKeys.includes("message") && <>
                                    <label>{translate("OTM_CUSTOM_CONDITION_CONTROL_PHRASES_7")}</label>

                                    <RequiredIndicator className="flex-grow-1 w-100">
                                        <GenericTextBox
                                            defaultValue={condition.message}
                                            disabled={disabled}
                                            onKeyDown={_handleKeyDown}
                                            onBlur={() => { blurConditionField(i, j, 'message') }}
                                            onChange={(event) => { updateConditionField(i, j, { 'message': event.target.value }) }}
                                            css={{ input: `form-control-sm w-100 ${getErrorBorder('message', i, j)}` }}
                                        />
                                    </RequiredIndicator>
                                </>}
                            </div>
                            {
                                !disabled && andConditions.length > 1 &&
                                <div className="ms-3 sentence-delete">
                                    <Button
                                        variant="outline-danger"
                                        className="p-0 border-0"
                                        onClick={() => { removeCondition(i, j) }}
                                        title={translate("OTM_DELETE")}
                                    ><Icon icon="Delete" className="d-block" /></Button>
                                </div>
                            }
                        </div>

                        {error(i, j)}
                        {j !== andConditions.length - 1 && <div className="py-2">
                            <label className='fw-bold'>{translate("OTM_AND")}</label>
                        </div>}
                    </Fragment>
                })}
            </>
    }, [name, formTemplate?.sections, filterSections, sharedSection, showConditionValue, getItemsWithCurrentUpdates, disabled, conditionKeys,
        getOperationOptions, error, getErrorBorder, renderRelativeValuePhrase, renderStrictValuePhrase, removeCondition, updateConditionField, translate, blurConditionField]);

    const renderGroupings = useCallback((orConditions) => {
        return !orConditions || orConditions.length === 0 ?
            null :
            <div className="row mt-n2">
                {orConditions.map((andConditions, i) => (<Fragment key={`${name}_condition_${i}`}>
                    <div className="col-12 mt-2">
                        <div className="card border h-100">
                            <div className="card-body d-flex flex-column">
                                {renderConditions(andConditions, i)}
                                {
                                    !disabled &&
                                    <div className="row gx-2 justify-content-end">
                                        <div className="col-auto mt-2">
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                className=" border-0"
                                                onClick={() => { addCondition(i); }}
                                            >{translate("OTM_ADD_AND")}</Button>
                                        </div>
                                        <div className="col-auto mt-2">
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                className=" border-0"
                                                onClick={() => { removeGrouping(i); }}
                                            >{translate("OTM_DELETE_GROUP")}</Button>
                                        </div>
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                    {i !== orConditions.length - 1 && <div className="col-12 mt-2">
                        <label className='fw-bold'>{translate("OTM_OR")}</label>
                    </div>}
                </Fragment>))}
            </div>
    }, [disabled, name, addCondition, removeGrouping, renderConditions, translate]);

    const renderAlert = useMemo(() => {
        if (name !== "readOnly" || itemReadOnly !== true) { return null; }

        return <Alert variant="warning">
            <span className="fw-bold">{translate("OTM_ITEM_IS_READONLY_WARNING")}</span>
        </Alert>;
    }, [name, itemReadOnly, translate]);

    const copyFromExistingFields = useMemo(() => !disabled && canCopy ? (
        <div>
            <span className='h5 mb-2'>{translate("OTM_CONDITIONS_COPY_FROM_EXISTING")}</span>
            <GenericDropdown
                grow={true}
                defaultValue={copyOptionId}
                isRequired={true}
                onSelect={setCopyOptionId}
                options={copyOptions}
                valueKey="_id"
                displayValueKey="name"
            />
            {copyOptionId ? (
                <Button
                    size="sm"
                    variant="primary"
                    className="mt-2"
                    onClick={() => {
                        const selectedOption = copyOptions.find((option) => option._id === copyOptionId);
                        if (selectedOption && selectedOption[name] && selectedOption[name].length > 0 && selectedOption[name].at(0).length > 0) {
                            const idReplacement = {
                                [sectionId]: sectionId,
                                [selectedOption._id]: item._id
                            };
                            const result = updateConditionIds(selectedOption[name], idReplacement);
                            setOrConditions(result);
                            handleFieldChange(result);
                        }
                    }}
                >
                    {translate("OTM_CONDITIONS_COPY")}
                </Button>
            ) : null
            }
        </div>
    ) : null, [disabled, canCopy, copyOptionId, copyOptions, sectionId, item._id, handleFieldChange, name, translate]);

    return <>
        {renderAlert}
        <label className='form-label h6'>{!orConditions || orConditions.length === 0 ? translate(labelNoStatements) : translate(label)}</label>
        {renderGroupings(orConditions)}
        {disabled ? null : (
            <div className="row gx-2 justify-content-end">
                <div className="col-auto mt-2">
                    <Button
                        id="btn_add_or_group"
                        size="sm"
                        variant="primary"
                        disabled={disabled}
                        onClick={() => { addGrouping() }}
                    >{translate(!orConditions || orConditions.length === 0 ? "OTM_ADD_OR_FIRST_STATEMENT" : "OTM_ADD_OR")}</Button>
                </div>
            </div>
        )}
        {copyFromExistingFields}
    </>
}

export default CustomConditionControl;

const RequiredIndicator = ({ className, children }) => {

    return <div className={`d-flex align-items-top ${className}`}>
        <span className='text-danger me-1'>*</span>
        {children}
    </div>
}